import { createFileRoute } from "@tanstack/react-router";
import { SITE_NAME, SITE_URL, absUrl, canonicalLink, canonicalUrl, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { WagonCoverForm } from "@/components/WagonCoverForm";
import hero from "@/assets/wagon-cover-cream-canvas-screen-windows-trailer-1920.webp";
import heroPicture from "@/assets/wagon-cover-cream-canvas-screen-windows-trailer-1920.webp?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import coveredWagon from "@/assets/wagon-covered-horse-drawn-texas-1920.webp";
import wagonBowFrame from "@/assets/historic-covered-wagon-bow-frame-texas-pasture.jpg";
import wagonInterior from "@/assets/wagon-cover-canvas-bow-interior.jpg";
import wagonRearArch from "@/assets/wagon-cover-rear-arch-canvas-trailer.jpg";
import wagonSideWindows from "@/assets/wagon-cover-side-screen-windows-trailer.jpg";

export const WAGON_FAQS = [
  { q: "Can you match a period-correct look?", a: "Yes. We can match traditional chuck wagon canvas weights and colors for restoration and display wagons across Texas — from Hill Country museum pieces to working ranch wagons." },
  { q: "What material do you use?", a: "Heavy 10oz–18oz cotton duck canvas for traditional looks, marine canvas for long-life covers, and vinyl-coated polyester for the heaviest working applications. All built to take Texas sun, wind, and rain." },
  { q: "Do I bring the wagon to you?", a: "Most wagon covers are pattern-fit at our Seguin shop just east of San Antonio. For larger restored wagons across the Hill Country and South Texas ranches, we can also pattern on-site by appointment." },
  { q: "How long does a custom wagon cover take?", a: "After we have your bow measurements (A, B, C) or an in-person pattern session, most custom wagon covers are sewn and ready in 2–4 weeks depending on shop schedule and material availability." },
  { q: "Do you ship wagon covers outside Texas?", a: "We build and install wagon covers within the state of Texas only. Pattern-fit and installation are available across the San Antonio metro, Austin, the Hill Country, the Gulf Coast, South Texas, and the Rio Grande Valley." },
  { q: "Can you add windows, doors, or a windshield?", a: "Yes. Our wagon cover quote form includes options for roll-up screen windows, doors, a front windshield piece, a removable transport windshield cap, and interior upholstery." },
];

export const Route = createFileRoute("/wagon-covers")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Custom Wagon Covers in Texas — Chuck Wagon, Hay & Utility Wagon Tops | Texas Made Shade",
      description: "Custom-built wagon covers and tops for chuck wagons, hay wagons, utility and parade wagons. Heavy-duty canvas and vinyl, patterned to your wagon.",
      image: hero,
      imageAlt: "Custom cream canvas wagon cover with zippered roll-up screen windows and heavy-duty webbing strap-downs, fabricated by Texas Made Shade over a working wood-sided trailer wagon in Seguin, Texas",
      socialTitle: "Custom Wagon Covers — Chuck, Hay & Parade Wagon Tops | Texas Made Shade",
      socialDescription: "Period-correct and working covered-wagon tops in heavy canvas and marine vinyl — hand-patterned to your wagon's bows and built in Seguin, Texas.",
      keywords: "wagon covers Texas, chuck wagon covers, hay wagon tops, utility wagon covers, custom wagon covers Seguin, covered wagon canvas top, parade wagon cover",
    }),
    scripts: [
      ...serviceHeadScripts({
        path: "/wagon-covers",
        serviceName: "Wagon Covers",
        breadcrumbName: "Wagon Covers",
        description: "Historic and working covered-wagon canvas covers — custom patterned and sewn by Texas Made Shade.",
        faqs: WAGON_FAQS,
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": `${canonicalUrl("/wagon-covers")}#local-business`,
          name: "Texas Made Shade — Custom Wagon Covers",
          parentOrganization: { "@id": `${SITE_URL}/#business` },
          url: canonicalUrl("/wagon-covers"),
          telephone: "+1-830-390-9588",
          email: "office@texasmadeshade.com",
          image: absUrl(hero),
          priceRange: "$$",
          address: {
            "@type": "PostalAddress",
            streetAddress: "959 East Kingsbury Street",
            addressLocality: "Seguin",
            addressRegion: "TX",
            postalCode: "78155",
            addressCountry: "US",
          },
          geo: { "@type": "GeoCoordinates", latitude: 29.5688, longitude: -97.9647 },
          openingHoursSpecification: [
            { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday"], opens: "08:30", closes: "17:00" },
            { "@type": "OpeningHoursSpecification", dayOfWeek: ["Friday"], opens: "08:30", closes: "16:00" },
          ],
          areaServed: [
            { "@type": "State", name: "Texas" },
            { "@type": "City", name: "Seguin", containedInPlace: { "@type": "State", name: "Texas" } },
            { "@type": "City", name: "San Antonio", containedInPlace: { "@type": "State", name: "Texas" } },
            { "@type": "City", name: "New Braunfels", containedInPlace: { "@type": "State", name: "Texas" } },
            { "@type": "City", name: "Austin", containedInPlace: { "@type": "State", name: "Texas" } },
            { "@type": "City", name: "Boerne", containedInPlace: { "@type": "State", name: "Texas" } },
            { "@type": "City", name: "Bandera", containedInPlace: { "@type": "State", name: "Texas" } },
            { "@type": "City", name: "Fredericksburg", containedInPlace: { "@type": "State", name: "Texas" } },
            { "@type": "City", name: "Kerrville", containedInPlace: { "@type": "State", name: "Texas" } },
          ],
          knowsAbout: [
            { "@type": "Service", name: "Chuck Wagon Covers" },
            { "@type": "Service", name: "Hay & Utility Wagon Covers" },
            { "@type": "Service", name: "Parade & Display Wagon Covers" },
            { "@type": "Service", name: "Antique Wagon Restoration & Re-Cover" },
          ],

          aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "47" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          // Modeled as a Service, not a Product: wagon covers are quote-priced
          // and Google's Product snippet requires offers/review/aggregateRating.
          "@type": "Service",
          "@id": `${canonicalUrl("/wagon-covers")}#product`,
          name: "Custom Wagon Covers — Chuck, Hay, Utility & Parade Wagon Tops",
          serviceType: "Custom Wagon Cover Fabrication",
          description:
            "Custom-built canvas wagon covers hand-patterned to your wagon's bows. Heavy 10oz–18oz cotton duck, marine canvas.",
          image: [absUrl(hero), absUrl(wagonInterior), absUrl(wagonRearArch)],
          brand: { "@type": "Brand", name: SITE_NAME },
          category: "Wagon Covers",
          url: canonicalUrl("/wagon-covers"),
          areaServed: { "@type": "State", name: "Texas" },
          provider: { "@id": `${SITE_URL}/#business` },

        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ImageObject",
          contentUrl: absUrl(hero),
          url: absUrl(hero),
          caption:
            "Custom cream canvas wagon cover with roll-up screen windows and webbing tie-downs over a wood-sided trailer wagon, fabricated by Texas Made Shade in Seguin, Texas",
          creditText: SITE_NAME,
          creator: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
          copyrightNotice: `© ${new Date().getFullYear()} ${SITE_NAME}`,
          license: SITE_URL,
          acquireLicensePage: canonicalUrl("/contact"),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to Measure a Covered Wagon for a Custom Canvas Cover",
          description:
            "Three measurements (A, B, C) to send Texas Made Shade for an accurate written quote on a custom canvas wagon top.",
          totalTime: "PT10M",
          tool: [
            { "@type": "HowToTool", name: "Tape measure (25 ft or longer)" },
            { "@type": "HowToTool", name: "Helper to hold the tape" },
          ],
          step: [
            {
              "@type": "HowToStep",
              position: 1,
              name: "Measure A — Side height",
              text: "From the top of the wagon sideboard, measure straight up the front bow to the peak of the arch.",
            },
            {
              "@type": "HowToStep",
              position: 2,
              name: "Measure B — Wagon length",
              text: "Measure along the side of the wagon from the front bow to the rear bow at sideboard height.",
            },
            {
              "@type": "HowToStep",
              position: 3,
              name: "Measure C — Bow over the top",
              text: "Run the tape over the front bow from one sideboard, across the top of the arch, down to the opposite sideboard.",
            },
          ],
        }),
      },
    ],
  }),
  component: () => (
    <ServicePage
      currentPath="/wagon-covers"
      serviceName="Wagon Covers"
      eyebrow="★ WAGON COVERS ★"
      title="Custom Wagon Covers in Texas — Chuck, Hay, Utility & Parade Wagon Tops"
      intro="Texas-built canvas wagon covers, hand-patterned in our Seguin, TX shop and shipped statewide — from the Hill Country to South Texas ranches. Heavy-duty cotton duck, marine canvas and vinyl tops engineered to take Texas sun, wind, and weather without sagging or fading. Call +1 (830) 390-9588 or request a free written estimate today."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "Cover Your Wagon",
        headline: "Get a Custom Texas Wagon Cover Quote",
        subhead: "Sewn in Seguin and installed across San Antonio, the Hill Country, and South Texas ranches — free written quote in 1–2 business days.",
        primaryHref: "#wagon-cover-quote",
        primaryLabel: "Request a Free Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "Patterned to your wagon's exact bows or frame",
        "Heavy-duty canvas, marine canvas, or vinyl",
        "Reinforced grommets, ties, and edges",
        "Chuck, hay, utility and parade wagon styles",
        "Period-correct looks for restored wagons",
        "Repair and re-cover service for vintage wagons",
      ]}
      body={[
        { heading: "Chuck Wagon Tops", text: "Period-correct or modern chuck wagon covers in heavy canvas. Built for cookouts, ranch use, and event display." },
        { heading: "Hay & Utility Wagon Covers", text: "Working covers for hay wagons, ranch utility wagons, and feed wagons — built to handle daily ranch use." },
        { heading: "Parade & Display Wagons", text: "Show-quality covers in your choice of canvas color and trim — built to look right and hold up to weekend parades and events." },
        { heading: "Restoration & Re-Cover", text: "Bringing an old wagon back? We'll match the original style and re-cover it in canvas built to last." },
      ]}
      applications={["Chuck Wagons", "Hay Wagons", "Ranch Utility Wagons", "Feed Wagons", "Parade Wagons", "Restored Antique Wagons"]}
      faqs={WAGON_FAQS}
      hideLetsTalkForm
      gallerySection={{
        eyebrow: "Wagon Cover Work",
        title: "Recent Wagon Covers",
        images: [
          { src: coveredWagon, alt: "Custom heavy canvas covered wagon top fabricated by Texas Made Shade on a horse-drawn parade and ranch wagon traveling a Central Texas country road, with a second covered wagon following behind", caption: "Horse-drawn covered wagon — custom canvas top patterned to the bows" },
        ],
      }}
      preContent={
        <>
          <section
            aria-label="Texas Made Shade trust signals"
            className="border-t-4 border-navy-deep bg-navy-deep py-5 text-white"
          >
            <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-4 text-center font-display text-xs uppercase tracking-[0.18em] sm:text-sm lg:px-8">
              <span className="inline-flex items-center gap-2">
                <span aria-hidden className="text-base text-yellow-300">★★★★★</span>
                <span>5.0 from 47+ Texas customers</span>
              </span>
              <span aria-hidden className="hidden h-4 w-px bg-white/30 sm:inline-block" />
              <span className="inline-flex items-center gap-2">
                <span aria-hidden className="text-red">●</span>
                <span>20+ years building covers in Texas</span>
              </span>
              <span aria-hidden className="hidden h-4 w-px bg-white/30 sm:inline-block" />
              <span className="inline-flex items-center gap-2">
                <span aria-hidden>🇺🇸</span>
                <span>Texas-made · Sewn in Seguin, TX</span>
              </span>
              <span aria-hidden className="hidden h-4 w-px bg-white/30 sm:inline-block" />
              <span className="inline-flex items-center gap-2">
                <span aria-hidden className="text-red">✓</span>
                <span>Free written quotes · 1–2 day reply</span>
              </span>
            </div>
          </section>
          <section className="border-t-4 border-navy-deep bg-cream py-12 sm:py-16">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <figure className="mb-6 sm:float-right sm:mb-3 sm:ml-6 sm:w-[44%] md:w-[40%] lg:w-[36%]">
              <img
                src={wagonBowFrame}
                alt="Historic gray wooden covered wagon with original spoked wheels and black steel bow frame ready for a custom canvas top, parked in a Texas Hill Country pasture"
                width={896}
                height={1195}
                className="block h-auto w-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <figcaption className="mt-2 text-xs uppercase tracking-[0.2em] text-navy-deep/70">
                Historic wagon · bow frame ready for canvas
              </figcaption>
            </figure>
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Texas Wagon Cover Builders</div>
            <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
              Custom Wagon Tops Patterned to Your Bows — Built in Seguin, Texas
            </h2>
            <p className="mt-4 text-lg text-navy-deep/80">
              Whether you&apos;re restoring an antique chuck wagon in the Hill Country, building a parade rig
              for a San Antonio rodeo, or putting a working hay wagon back in service on a South Texas
              ranch, Texas Made Shade hand-patterns every wagon cover directly to your bow frame. The
              canvas pulls drum-tight, sheds rain, and holds its shape through Texas heat, wind, and
              thunderstorms. Choose heavy 10oz–18oz cotton duck for period-correct restorations, or
              marine canvas and vinyl-coated polyester when you need a working wagon top that lasts
              decades on the ranch.
            </p>
            <p className="mt-4 text-lg text-navy-deep/80">
              Every cover is finished the right way — reinforced edges, solid brass grommets, rope
              channels, and traditional tie-downs sewn on heavy-duty industrial machines. Bring your
              wagon to our Seguin shop just east of San Antonio, or send photos and bow measurements
              and we&apos;ll send a written quote within 1–2 business days.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="#wagon-cover-quote"
                className="inline-flex items-center bg-red px-6 py-3 font-display text-sm uppercase tracking-wider text-white transition hover:bg-red/90"
              >
                ★ Request a Free Wagon Cover Quote
              </a>
              <a
                href="tel:+18303909588"
                className="inline-flex items-center border-2 border-navy-deep bg-white px-6 py-3 font-display text-sm uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
              >
                Call +1 (830) 390-9588
              </a>
            </div>
            <div className="clear-both" />

            <div className="mt-12 grid gap-8 sm:mt-14 sm:gap-10 lg:grid-cols-[5fr_4fr] lg:items-center">
              <figure className="overflow-hidden border-4 border-navy-deep shadow-card">
                <img
                  src={wagonInterior}
                  alt="Interior view from inside a custom Texas Made Shade covered wagon — heavy canvas top stretched across steel bow frame with reinforced binding tape, framing two red-and-brown leather Chevrolet bench seats and an arched rear opening"
                  width={1080}
                  height={1920}
                  className="block h-auto w-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-[0.2em] text-white">
                  Interior · canvas top &amp; steel bow frame from inside the wagon
                </figcaption>
              </figure>
              <div>
                <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Inside the Cover</div>
                <h3 className="mt-2 font-display text-2xl font-bold text-navy-deep md:text-3xl">
                  Heavy Canvas, Reinforced Seams, Built Around the Bows
                </h3>
                <p className="mt-4 text-lg text-navy-deep/80">
                  From inside the wagon you can see how every panel is patterned to the bow spacing — the canvas pulls
                  drum-tight across each rib, with reinforced binding tape running along every seam to take the load
                  instead of the stitching. The arched front and rear openings are bound and gathered so the cover keeps
                  its shape when the wagon is in motion.
                </p>
                <p className="mt-4 text-lg text-navy-deep/80">
                  The bow frame stays exposed on the inside the way a working wagon should — clean lines, no flapping
                  fabric, no sagging panels. Whether the wagon hauls hunters, hay, or hand-built leather seats like the
                  Chevy bench in this build, the cover is built to ride hard and look right for decades.
                </p>
              </div>
            </div>
          </div>
          </section>
          <WagonCoverForm />
        </>
      }
    >

      <section className="border-t-4 border-navy-deep bg-white py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Modern Trailer-Wagon Build</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            Covered-Wagon Top on a Working Trailer Chassis
          </h2>
          <p className="mt-4 max-w-3xl text-lg text-navy-deep/80">
            Not every covered wagon is horse-drawn anymore. We pattern and build the same heavy-canvas tops over modern
            trailer chassis — for hunting camps, glamping rigs, ranch hospitality, and tailgate setups that still want
            the classic prairie-schooner silhouette. Steel bows under the canvas, marine-grade webbing straps over the
            top, and a removable rear closure that buttons up tight when the rig is on the road.
          </p>

          <div className="mt-10 grid gap-8 sm:mt-12 sm:gap-10 lg:grid-cols-2 lg:items-start">
            <figure className="overflow-hidden border-4 border-navy-deep shadow-card">
              <img
                src={wagonRearArch}
                alt="Custom natural-canvas covered wagon top fabricated by Texas Made Shade over a wood-sided utility trailer chassis, showing the rear arched opening, gathered drawstring closure, and bow-framed canvas roof"
                width={1440}
                height={1920}
                className="block h-auto w-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-[0.2em] text-white">
                Rear arch · gathered canvas closure
              </figcaption>
            </figure>
            <div className="lg:pt-4">
              <h3 className="font-display text-2xl font-bold text-navy-deep md:text-3xl">
                Arched Rear Opening, Drawstring Closure
              </h3>
              <p className="mt-4 text-lg text-navy-deep/80">
                The rear of the cover is patterned as a true arch — bound around the bow with reinforced webbing and
                gathered with a drawstring so it pulls in tight against weather or opens wide for loading. Inside, the
                amber under-cap is the original canvas; the new outer skin in light natural duck adds a second
                weather layer and refreshes the look without losing the period silhouette.
              </p>
              <ul className="mt-6 grid gap-3 text-base text-navy-deep/85">
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-red" />
                  Heavy natural duck outer cover, double-layered over the original cap
                </li>
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-red" />
                  Bound webbing edges follow every steel bow
                </li>
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-red" />
                  Drawstring rear closure for tight or wide-open access
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 grid gap-8 sm:mt-16 sm:gap-10 lg:grid-cols-2 lg:items-start">
            <div className="lg:pt-4 lg:order-1">
              <h3 className="font-display text-2xl font-bold text-navy-deep md:text-3xl">
                Roll-Up Screen Windows &amp; Strap-Down Sides
              </h3>
              <p className="mt-4 text-lg text-navy-deep/80">
                The side panels carry two roll-up screen windows with buckle-down flaps — open them for cross-breeze
                on a hunting stand or on a hot Texas afternoon, snap them shut when the dust kicks up. Heavy black
                webbing straps run from the canvas hem down to the trailer rail at every bow, anchoring the cover
                against highway wind and storm gusts.
              </p>
              <ul className="mt-6 grid gap-3 text-base text-navy-deep/85">
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-red" />
                  Two zippered, roll-up screen windows per side
                </li>
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-red" />
                  Buckle-down storm flaps over every screen
                </li>
                <li className="flex items-start gap-2">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 bg-red" />
                  Marine-grade webbing tie-downs at every bow
                </li>
              </ul>
              <p className="mt-6 text-base text-navy-deep/75">
                Bring us your trailer or wagon — or send measurements of the bows and sideboards — and we&apos;ll build
                a cover that handles Texas weather and looks the part wherever it parks.
              </p>
            </div>
            <figure className="overflow-hidden border-4 border-navy-deep shadow-card lg:order-2">
              <img
                src={wagonSideWindows}
                alt="Side view of a custom Texas Made Shade covered wagon top on a brown wood-sided trailer, showing two roll-up screen window flaps with buckle closures and black webbing tie-down straps running down each bow to the trailer frame"
                width={1440}
                height={1920}
                className="block h-auto w-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-[0.2em] text-white">
                Side panel · roll-up screens &amp; webbing tie-downs
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section
        aria-label="Wagon cover customer reviews and project proof"
        className="border-t-4 border-navy-deep bg-cream py-12 sm:py-16"
      >
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="text-center">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
              ★ Texas Owners · Real Wagons
            </div>
            <h2 className="mt-2 font-display text-2xl font-bold text-navy-deep sm:text-3xl md:text-4xl">
              5.0 Stars from 47+ Texas Wagon Owners
            </h2>
            <p className="mt-3 text-base text-navy-deep/75 sm:text-lg">
              Chuck wagons, parade rigs, hay haulers and trailer-built prairie schooners — patterned and sewn in Seguin.
            </p>
          </div>

          <div className="mt-8 grid gap-6 sm:mt-10 md:grid-cols-3">
            {[
              {
                quote:
                  "Patterned the canvas right to our chuck wagon bows — drum-tight and looks period-correct. Best cover we've ever had on this rig.",
                author: "Cody R.",
                location: "Bandera, TX",
              },
              {
                quote:
                  "Sewn the same week we sent measurements. Marine canvas has handled two Hill Country summers without sagging or fading.",
                author: "Marisol G.",
                location: "Fredericksburg, TX",
              },
              {
                quote:
                  "Built a covered-wagon top over our trailer chassis for hunting camp. Roll-up screens and webbing tie-downs are first class.",
                author: "Wes T.",
                location: "South Texas Ranch",
              },
            ].map((r) => (
              <figure
                key={r.author}
                className="flex h-full flex-col border-4 border-navy-deep bg-white p-5 shadow-card sm:p-6"
              >
                <div aria-hidden className="font-display text-base text-yellow-500">
                  ★★★★★
                </div>
                <blockquote className="mt-3 flex-1 text-base text-navy-deep/85">
                  &ldquo;{r.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-4 font-display text-xs uppercase tracking-[0.18em] text-navy-deep">
                  {r.author} · <span className="text-red">{r.location}</span>
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="mt-8 grid gap-3 border-y-2 border-navy-deep/15 py-5 text-center font-display text-xs uppercase tracking-[0.18em] text-navy-deep sm:mt-10 sm:grid-cols-4 sm:text-sm">
            <div><span className="block text-2xl font-bold text-red sm:text-3xl">20+</span>Years building covers</div>
            <div><span className="block text-2xl font-bold text-red sm:text-3xl">500+</span>Texas wagons & trailers</div>
            <div><span className="block text-2xl font-bold text-red sm:text-3xl">5.0★</span>From 47+ reviews</div>
            <div><span className="block text-2xl font-bold text-red sm:text-3xl">1–2 Day</span>Quote turnaround</div>
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-3 sm:mt-10">
            <a
              href="#wagon-cover-quote"
              className="inline-flex items-center bg-red px-6 py-3 font-display text-sm uppercase tracking-wider text-white transition hover:bg-red/90"
            >
              ★ Request Your Free Quote
            </a>
            <a
              href="tel:+18303909588"
              className="inline-flex items-center border-2 border-navy-deep bg-white px-6 py-3 font-display text-sm uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
            >
              Call +1 (830) 390-9588
            </a>
          </div>
        </div>
      </section>
    </ServicePage>
  ),
});
